import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Video, Clock, ShieldCheck, Headphones, Wifi, DoorClosed,
  AlertCircle, Star, ArrowRight, Maximize2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import api, { mensajeDeError } from '../lib/api';
import { toast } from 'sonner';

/**
 * Sala de sesión.
 *
 * El enlace que llega por correo apunta acá, no a la videollamada directamente.
 * El servidor valida la llave y solo entrega la sala dentro del horario: antes
 * se ve una cuenta regresiva, después el enlace deja de servir.
 *
 * Cada parte tiene su propia llave, así que un enlace reenviado no sirve para
 * entrar como la otra persona.
 */
const SesionPage = () => {
  const { token } = useParams();

  const [sesion, setSesion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [entrando, setEntrando] = useState(false);
  const [restante, setRestante] = useState(null);
  const temporizador = useRef(null);

  const consultar = useCallback(async () => {
    try {
      const { data } = await api.get(`/sesion/${token}`);
      setSesion(data);
      setError(null);
      return data;
    } catch (err) {
      setError(mensajeDeError(err, 'Este enlace de sesión no es válido'));
      return null;
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => {
    consultar();
  }, [consultar]);

  // Cuenta regresiva hasta que abre la sala. Al llegar a cero se vuelve a
  // preguntar al servidor en vez de abrir por cuenta propia.
  useEffect(() => {
    if (!sesion || sesion.state !== 'waiting') {
      setRestante(null);
      return undefined;
    }

    setRestante(sesion.seconds_until_open);

    temporizador.current = setInterval(() => {
      setRestante((s) => {
        if (s === null) return null;
        if (s <= 1) {
          consultar();
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(temporizador.current);
  }, [sesion, consultar]);

  const entrar = async () => {
    setEntrando(true);
    const datos = await consultar();
    setEntrando(false);

    if (datos?.state !== 'open') {
      toast.error('La sala todavía no está abierta');
    }
  };

  if (cargando) {
    return (
      <Marco>
        <p className="text-slate-500 text-center">Preparando tu sesión…</p>
      </Marco>
    );
  }

  if (error) {
    return (
      <Marco>
        <Aviso
          icono={AlertCircle}
          tono="slate"
          titulo="Este enlace no es válido"
          texto="Puede que esté mal copiado o que corresponda a una sesión muy antigua."
        />
        <div className="text-center mt-6">
          <Link to="/mis-horas">
            <Button variant="outline" className="rounded-full">
              Ver mis horas
            </Button>
          </Link>
        </div>
      </Marco>
    );
  }

  const esProfesional = sesion.role === 'psychologist';

  // ---------- Sala abierta ----------
  if (sesion.state === 'open') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-800 border-b border-slate-700">
          <div className="min-w-0">
            <p className="text-white font-medium truncate">Sesión con {sesion.with}</p>
            <p className="text-xs text-slate-400">
              {sesion.duration_minutes} minutos · conexión cifrada
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-teal-300 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              En curso
            </span>
            <a href={sesion.meeting_url} target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Maximize2 className="w-4 h-4 mr-1.5" />
                Abrir aparte
              </Button>
            </a>
          </div>
        </div>

        <iframe
          title="Sesión de videollamada"
          src={sesion.meeting_url}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          className="flex-1 w-full border-0"
        />
      </div>
    );
  }

  // ---------- Antes de la hora ----------
  if (sesion.state === 'waiting') {
    const inicio = sesion.scheduled_date ? parseISO(sesion.scheduled_date) : null;

    return (
      <Marco>
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
            <Clock className="w-8 h-8 text-teal-700" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
            Tu sesión con {sesion.with}
          </h1>
          {inicio && (
            <p className="text-slate-600 first-letter:uppercase">
              {format(inicio, "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es })} hrs
            </p>
          )}
        </div>

        <Card className="border-teal-200 bg-teal-50/50 mb-6">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-teal-800 mb-3">La sala se abre en</p>
            <p className="font-mono text-4xl md:text-5xl font-semibold text-slate-900 tabular-nums">
              {formatearRestante(restante ?? sesion.seconds_until_open)}
            </p>
            <p className="text-xs text-teal-700 mt-3">
              Se habilita automáticamente. No necesitas hacer nada.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-6">
            <p className="font-medium text-slate-900 mb-4">Mientras esperas</p>
            <div className="space-y-3">
              <Consejo
                icono={DoorClosed}
                texto="Busca un lugar donde nadie te interrumpa y puedas hablar tranquilo."
              />
              <Consejo
                icono={Headphones}
                texto="Usa audífonos si puedes: mejora el sonido y cuida tu privacidad."
              />
              <Consejo
                icono={Wifi}
                texto="Revisa tu conexión. Si es inestable, acércate al router."
              />
              {!esProfesional && (
                <Consejo
                  icono={ShieldCheck}
                  texto="Tu profesional te verá solo con tu seudónimo. No tienes que dar tu nombre real."
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Button
          onClick={entrar}
          disabled={entrando}
          variant="outline"
          className="w-full rounded-full mt-6"
        >
          {entrando ? 'Revisando…' : 'Ya es la hora, entrar'}
        </Button>
      </Marco>
    );
  }

  // ---------- Después de la sesión ----------
  if (sesion.state === 'ended') {
    return (
      <Marco>
        <Aviso
          icono={Video}
          tono="slate"
          titulo="Esta sesión ya terminó"
          texto="El enlace de la sala deja de funcionar cuando la sesión finaliza."
        />

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          {sesion.review_token && (
            <Link to={`/valorar/${sesion.review_token}`} className="flex-1">
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                <Star className="w-4 h-4 mr-2" />
                Valorar la sesión
              </Button>
            </Link>
          )}
          <Link to={esProfesional ? '/profesional/dashboard' : '/mis-horas'} className="flex-1">
            <Button variant="outline" className="w-full rounded-full">
              {esProfesional ? 'Ir a mi panel' : 'Ver mis horas'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </Marco>
    );
  }

  // ---------- Cancelada o sin pagar ----------
  return (
    <Marco>
      <Aviso
        icono={AlertCircle}
        tono={sesion.state === 'cancelled' ? 'red' : 'amber'}
        titulo={sesion.state === 'cancelled' ? 'Esta sesión fue cancelada' : 'Falta completar el pago'}
        texto={sesion.message}
      />

      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        {sesion.state === 'unpaid' && sesion.booking_id && (
          <Link to={`/pago/resultado?reserva=${sesion.booking_id}`} className="flex-1">
            <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              Completar el pago
            </Button>
          </Link>
        )}
        <Link to={esProfesional ? '/profesional/dashboard' : '/mis-horas'} className="flex-1">
          <Button variant="outline" className="w-full rounded-full">
            {esProfesional ? 'Ir a mi panel' : 'Ver mis horas'}
          </Button>
        </Link>
      </div>
    </Marco>
  );
};

const Marco = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-lg">{children}</div>
  </div>
);

const Consejo = ({ icono: Icono, texto }) => (
  <div className="flex gap-3">
    <Icono className="w-5 h-5 text-teal-600 flex-none mt-0.5" />
    <p className="text-sm text-slate-600 leading-relaxed">{texto}</p>
  </div>
);

const Aviso = ({ icono: Icono, tono, titulo, texto }) => {
  const tonos = {
    slate: 'bg-slate-100 text-slate-600',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="text-center">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${
          tonos[tono] || tonos.slate
        }`}
      >
        <Icono className="w-8 h-8" />
      </div>
      <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">{titulo}</h1>
      <p className="text-slate-600">{texto}</p>
    </div>
  );
};

/** Segundos a un formato legible: 3 días, 2h 15m, o 15:30. */
function formatearRestante(segundos) {
  if (segundos === null || segundos === undefined) return '—';
  if (segundos <= 0) return '¡Ya!';

  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;

  if (h >= 24) {
    const dias = Math.floor(h / 24);
    return `${dias} ${dias === 1 ? 'día' : 'días'}`;
  }
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default SesionPage;
